import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { errorService } from './errorService';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a numeric value as currency in USD format.
 *
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Formats a date string from ISO format to MM/DD/YYYY format.
 * Handles both date-only (YYYY-MM-DD) and date-time (YYYY-MM-DDTHH:mm:ss) formats.
 *
 * @param dateString - The ISO date string to format
 * @returns Formatted date string in MM/DD/YYYY format, or '--' for invalid dates
 */
export function formatDate(dateString: string): string {
  try {
    // Handle empty or null input
    if (!dateString || typeof dateString !== 'string') {
      return '--';
    }

    // Check if the string already contains a time component (has 'T' in it)
    // If not, append 'T00:00:00' to ensure local timezone interpretation
    const dateInput = dateString.includes('T')
      ? dateString
      : `${dateString}T00:00:00`;

    const date = new Date(dateInput);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      errorService.reportError(
        new Error(`Invalid date string provided: "${dateString}"`),
        {
          operation: 'formatDate',
          input: dateString,
          processedInput: dateInput,
          error_type: 'date_parsing',
        }
      );
      return '--';
    }

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch (error) {
    // Report unexpected errors to Sentry
    errorService.reportError(error, {
      operation: 'formatDate',
      input: dateString,
      error_type: 'date_formatting_exception',
    });
    return '--';
  }
}

/**
 * Formats a numeric value as a percentage with the % symbol.
 *
 * @param value - The numeric value to format as percentage
 * @returns Formatted percentage string (e.g., "25.5%")
 */
export function formatPercentage(value: number): string {
  return `${value}%`;
}

/**
 * Safely parses a string value to a number, returning 0 if parsing fails.
 *
 * @param value - The string value to parse
 * @returns The parsed number or 0 if parsing fails
 */
export function safeParseFloat(value: string): number {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}
